import { useEffect, useRef, useState } from 'react';
import { Button, Card, Space, Row, Col, Table, message, Select, Input, Tooltip, Modal, Form, Divider, Popconfirm, Result } from 'antd';
import "../customerOrders.scss";
import { usePageTitle } from '../../../context/pageTitleContext';
import { useTranslation } from 'react-i18next';
import { dateFormat, menuLinks, orderStatusesCode, selectOptionsTypes, tablePageSize } from '../../../utils/constants';
import { useUserAuth } from '../../../context/UserContext';
import CreditNotesServices from '../../../services/creditNotesServices';
import { DeleteOutlined, ExclamationCircleOutlined, EyeOutlined, FilterOutlined, MailOutlined, SearchOutlined, UploadOutlined, WarningOutlined } from '@ant-design/icons';
import DynamicSelect from '../../../components/common/dynamicSelect';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { Link } from 'react-router';
import UploadCreditNoteModal from './uploadCreditNote';
import { Can, permissions, roles } from '../../../utils/permissionUtils';
import CustomerOrdersServices from '../../../services/customerOrdersServices';
import ResultModal from '../../../components/common/resultModal';
import { ResultModalState } from '../../../types/commonTypes';
import { getCountryLabel } from '../../../utils/functions';

const initialFilters = {
    soldTo: null,
    shipTo: null,
    orderType: null,
    orderReason: null,
    claimType: null,
    purchaseOrderType: null,
    salesDocument: null,
    material: null,
    orderStatus: null,
};


const CreditNote = () => {
    const { setPageTitle } = usePageTitle();
    const [form] = Form.useForm();
    const { t } = useTranslation();
    const { user } = useUserAuth();
    const [filters, setFilters] = useState(initialFilters);
    const [pageNumber, setPageNumber] = useState(1);
    const [sorterColumn, setSorterColumn] = useState("");
    const [creditNoteList, setCreditNoteList] = useState([]);
    const [loadingCreditNoteList, setLoadingCreditNoteList] = useState(false);
    const [totalCountRecords, setTotalCountRecords] = useState(null);
    const [activeFilteredColumn, setActiveFilteredColumn] = useState<any[]>([]);
    const orderStatuses = useSelector((state: any) => state.utils.orderStatuses);
    const [searchSalesDocument, setSearchSalesDocument] = useState("");
    const [uploadCreditNoteModal, setUploadCreditNoteModal] = useState(false);
    const [loadingRpaFiles, setLoadingRpaFiles] = useState(false);
    const [rpaCreditNoteCountryModal, setRpaCreditNoteCountryModal] = useState(false);

    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [selectedCreditNoteIds, setSelectedCreditNoteIds] = useState([]);
    const [selectedCreditNoteItemsIds, setSelectedCreditNoteItemsIds] = useState([]);
    const [sendingSelected, setSendingSelected] = useState(false);
    const rowKeyToCreditNoteIdRef = useRef(new Map());
    const rowKeyToCreditNoteItemsIdRef = useRef(new Map());

    const [resultModal, setResultModal] = useState<ResultModalState>({
        open: false,
        status: 'info',
        title: '',
        subTitle: '',
    });

    const userCountries = useSelector((state: any) => state.utils.userCountries);

    useEffect(() => {
        getCreditNoteList(filters);
    }, [pageNumber, sorterColumn, filters]);

    useEffect(() => {
        if (!user) return;
        setPageTitle(t('page.creditNote'));
    }, []);

    const getCreditNoteList = async (creditNoteFilters: any) => {

        let filterObj = creditNoteFilters;
        filterObj["pageNumber"] = pageNumber;
        filterObj["sortColumn"] = sorterColumn;
        filterObj["pageSize"] = tablePageSize;
        filterObj["userID"] = user.id;

        setLoadingCreditNoteList(true);
        try {
            const respData = await CreditNotesServices.getCreditNoteList(filterObj);

            if (respData.success) {
                const res = respData.responseObject.value.creditNotesListModel;

                const buildRowKey = (item: any, idx: number) => {
                    // Compact fingerprint from your most stable fields, then disambiguate with index
                    const fp = `${item.headerID ?? ''}|${item.itemsID ?? ''}|${item.material ?? ''}|${item.shipToCode ?? ''}|${item.soldToCode ?? ''}`;
                    // Use absolute index across pagination to avoid collisions between pages
                    const absoluteIdx = ((pageNumber - 1) * tablePageSize) + idx;
                    return `${fp}__at__${absoluteIdx}`;
                };

                const preparedData = res.map((item: any, idx: number) => ({
                    ...item,
                    rowKey: buildRowKey(item, idx),
                }));

                if (res && res.length > 0)
                    setTotalCountRecords(res[0].totalCount);

                // setCreditNoteList(res);
                setCreditNoteList(preparedData);

                const mId = rowKeyToCreditNoteIdRef.current;
                const mCreditNoteItemsId = rowKeyToCreditNoteItemsIdRef.current;
                preparedData.forEach((r: any) => {
                    mId.set(r.rowKey, String(r.headerID));
                    const creditNoteItemsId = r.itemsID ?? r.itemsID;
                    if (creditNoteItemsId !== undefined && creditNoteItemsId !== null) {
                        mCreditNoteItemsId.set(r.rowKey, String(creditNoteItemsId));
                    }
                });

            } else {
                message.error(t('messages.errorAccured'));
            }
        } catch (error) {
            message.error(t('messages.errorAccured'));
        } finally {
            setLoadingCreditNoteList(false);
        }
    };

    const onPageOrOrderChange = (pagination: any, filters: any, sorter: any) => {
        setPageNumber(pagination.current);
        if (sorter && sorter.field) {
            let sorterQuote = "";
            if (sorter.order === "descend") {
                sorterQuote = "[" + sorter.field + "]" + " " + "DESC";
            }
            if (sorter.order === "ascend") {
                sorterQuote = "[" + sorter.field + "]" + " " + "ASC";
            }
            setSorterColumn(sorterQuote);
        } else {
            setSorterColumn("");
        }
    };

    // const afterOrderListUpdate = () => {
    //     getCreditNoteList(filters);
    //     setSelectedCreditNoteIds([]);
    //     setSelectedCreditNoteItemsIds([]);
    //     setSelectedRowKeys([]);
    // }

    const onClearFilters = () => {
        setFilters(initialFilters);
        setPageNumber(1);
        // getCreditNoteList(initialFilters);
        setActiveFilteredColumn([]);
    }

    const manageSelectedFilters = (filterName: string, filterValue: any) => {
        setActiveFilteredColumn((prev: any[]) => {
            if (filterValue) {
                if (!prev.includes(filterName)) {
                    return [...prev, filterName];
                } else {
                    return prev; // already in array → do nothing
                }
            } else {
                return prev.filter((item) => item !== filterName);
            }
        });
    };

    const handelFilterChange = (name: string, value: any) => {
        manageSelectedFilters(name, value);
        setFilters((prev) => ({ ...prev, [name]: value }));
    }

    const handelSelectChangeFilter = (type: string, selectedOption: any) => {
        handelFilterChange(type, selectedOption?.value);
    };

    const onRemoveCreditNote = async (id: string) => {
        try {
            const respData = await CreditNotesServices.deleteCreditNote(id);

            if (respData.success) {
                message.success(t('messages.success'));
                getCreditNoteList(filters);
                setSelectedCreditNoteIds([]);
                setSelectedCreditNoteItemsIds([]);
                setSelectedRowKeys([]);
            }
            else {
                message.error(t('messages.errorAccured'));
            }

        } catch (error) {
            message.error(t('messages.errorAccured'));
        }
    }

    const onMultiDeleteCreditNotes = async () => {
        setSendingSelected(true);
        console.log("selectedCreditNoteIds", selectedCreditNoteIds);
        try {
            const respData = await CreditNotesServices.multiDeleteCreditNotes(selectedCreditNoteIds);

            if (respData.success) {
                message.success(t('messages.success'));
                getCreditNoteList(filters);
                setSelectedCreditNoteIds([]);
                setSelectedCreditNoteItemsIds([]);
                setSelectedRowKeys([]);

            }
            else {
                message.error(t('messages.errorAccured'));
            }

        } catch (error) {
            message.error(t('messages.errorAccured'));
        }
        finally {
            setSendingSelected(false);
        }
    };

    const onSendSelectedOrders = async () => {
        if (!selectedCreditNoteIds.length) {
            message.warning(t('messages.noCreditNotesSelected'));
            return;
        }
        onMultiDeleteCreditNotes()
    };

    const rowSelection = {
        selectedRowKeys,
        columnTitle: ' ',
        hideSelectAll: true,
        renderCell: (checked: any, record: any, index: number, originNode: any) => {
            if (record.orderStatusCode === orderStatusesCode.sendToRpa) {
                return null; // hide checkbox for send to RPA rows
            }
            return originNode; // default checkbox
        },
        preserveSelectedRowKeys: true,
        onChange: (newSelectedRowKeys: any) => {
            //update selected order header ids     
            
            setSelectedRowKeys(newSelectedRowKeys);
            const ids = (newSelectedRowKeys || []).map((k: any) => rowKeyToCreditNoteIdRef.current.get(k)).filter(Boolean);
            const uniqueIds = Array.from(new Set(ids));

            // update selected item ids (orderItemsID values) - keep all creditNoteItemsIds, no deduplication
            setSelectedCreditNoteIds(uniqueIds as any);
            const creditNoteItemsIds = (newSelectedRowKeys || []).map((k: any) => rowKeyToCreditNoteItemsIdRef.current.get(k)).filter(Boolean);
            setSelectedCreditNoteItemsIds(creditNoteItemsIds as any);
        }
    };


    const columns: any[] = [
        // {
        //     title: '',
        //     key: 'action',
        //     width: "30px",
        //     fixed: 'left',
        //     render: (text: any, record: any) => {
        //         return (
        //             <Space size="middle" direction='horizontal' style={{ columnGap: 5 }}>
        //                 <Link to={{ pathname: `${menuLinks.creditNoteDetails}/view/${record.headerID}` }}>
        //                     <EyeOutlined style={{ color: '#023B72' }} className='editIcon' />
        //                 </Link>
        //             </Space>
        //         )
        //     }
        // },
        {
            title: '',
            key: 'action',
            width: "30px",
            fixed: 'left',
            render: (text: any, record: any) => {

                return (
                    <Space size="middle" direction='horizontal' style={{ columnGap: 5 }}>                           

                        <Can permission={permissions.DELETE_CREDIT_NOTE}>
                            {record.deleteEnabled && (
                                <>
                                    {/* <Divider type="vertical" style={{ marginInline: 10 }} /> */}
                                    <Popconfirm
                                        placement="topRight"
                                        key="remove"
                                        title={t('messages.confirmationApprove')}
                                        icon={<WarningOutlined style={{ color: '#f7c924' }} />}
                                        okText={t('general.yes')}
                                        cancelText={t('general.no')}
                                        onConfirm={() => onRemoveCreditNote(record.headerID)}
                                        disabled={selectedCreditNoteIds.length > 0}
                                    >
                                        <DeleteOutlined style={ selectedCreditNoteIds.length > 0 ? { color: '#999', cursor: 'not-allowed' } : { color: '#023B72' }} />
                                    </Popconfirm>
                                </>
                            )}
                        </Can>
                    </Space>
                )
            }
        },
        {
            title: t('order.soldToCode'),
            dataIndex: 'soldToCode',
            key: 'soldToCode',
            showSorterTooltip: false,
            ellipsis: true,
            filterDropdown: ({ confirm }: { confirm: any }) => (
                <div style={{ padding: 8, width: "200px" }}>
                    <DynamicSelect type={selectOptionsTypes.soldTo} value={filters.soldTo} onChange={() => { }} location={''} onSelectChange={handelSelectChangeFilter} />
                </div>
            ),
            filterIcon: (
                <FilterOutlined
                    style={{
                        color: activeFilteredColumn.includes('soldTo') ? '#1677ff' : '#999', // blue if active, gray if not
                    }}
                />
            )
        },
        {
            title: t('order.soldToName'),
            dataIndex: 'soldToName',
            key: 'soldToName',
            ellipsis: true,
            showSorterTooltip: false
        },
        {
            title: t('order.shipToCode'),
            dataIndex: 'shipToCode',
            key: 'shipToCode',
            ellipsis: true,
            showSorterTooltip: false,
            filterDropdown: () => (
                <div style={{ padding: 8, width: "200px" }}>
                    <DynamicSelect type={selectOptionsTypes.shipTo} value={filters.shipTo} onChange={() => { }} location={''} onSelectChange={handelSelectChangeFilter} />
                </div>
            ),
            filterIcon: (
                <FilterOutlined
                    style={{
                        color: activeFilteredColumn.includes('shipTo') ? '#1677ff' : '#999', // blue if active, gray if not
                    }}
                />
            )
        },
        {
            title: t('order.shipToName'),
            dataIndex: 'shipToName',
            key: 'shipToName',
            ellipsis: true,
            showSorterTooltip: false,
        },
        {
            title: t('general.status'),
            dataIndex: 'orderStatusName',
            key: 'orderStatusName',
            showSorterTooltip: false,
            ellipsis: true,
            filterDropdown: () => (
                <div style={{ padding: 8, width: "200px" }}>
                    <Select
                        size='small'
                        style={{ width: "100%" }}
                        showSearch
                        allowClear
                        value={filters.orderStatus}
                        onChange={(val: any, option: any) => {
                            handelFilterChange("orderStatus", val);
                        }}
                        placeholder={t('general.select')}
                        optionFilterProp="label"
                        options={orderStatuses.map((item: any) => ({ value: item.id, label: item.description }))}
                    >
                    </Select>

                </div>
            ),
            filterIcon: (
                <FilterOutlined
                    style={{
                        color: activeFilteredColumn.includes('orderStatus') ? '#1677ff' : '#999', // blue if active, gray if not
                    }}
                />
            )
        },
        {
            title: t('order.salesDocument'),
            dataIndex: 'salesDocument',
            key: 'salesDocument',
            showSorterTooltip: false,
            ellipsis: true,
            width: "200px",
            render: (text: any) => (
                <Tooltip placement="topLeft" title={text}>
                    <span
                        style={{
                            display: "inline-block",
                            maxWidth: "200px",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            verticalAlign: "middle",
                        }}
                    >
                        {text}
                    </span>
                </Tooltip>
            ),
            filterDropdown: () => (
                <div style={{ padding: 8, width: "200px" }}>
                    <Space>
                        <Input placeholder='Search' value={searchSalesDocument} size='small' allowClear onClear={() => handelFilterChange("salesDocument", null)} onChange={(e) => setSearchSalesDocument(e.target.value)} />
                        <Button type='dashed' size='small' icon={<SearchOutlined />} onClick={(e) => handelFilterChange("salesDocument", searchSalesDocument)}></Button>
                    </Space>
                </div>
            ),
            filterIcon: (
                <FilterOutlined
                    style={{
                        color: activeFilteredColumn.includes('salesDocument') ? '#1677ff' : '#999', // blue if active, gray if not
                    }}
                />
            )
        },
        {
            title: t('order.material'),
            dataIndex: 'material',
            key: 'material',
            showSorterTooltip: false,
            ellipsis: true,
            filterDropdown: () => (
                <div style={{ padding: 8, width: "200px" }}>
                    <DynamicSelect type={selectOptionsTypes.material} value={filters.material} onChange={() => { }} location={''} onSelectChange={handelSelectChangeFilter} />
                </div>
            ),
            filterIcon: (
                <FilterOutlined
                    style={{
                        color: activeFilteredColumn.includes('material') ? '#1677ff' : '#999', // blue if active, gray if not
                    }}
                />
            )
        },
        {
            title: t('order.materialType'),
            dataIndex: 'materialType',
            key: 'materialType',
            showSorterTooltip: false,
            ellipsis: true
        },
        {
            title: t('order.serialNumber'),
            dataIndex: 'serialNumber',
            key: 'serialNumber',
            showSorterTooltip: false,
            ellipsis: true
        },
        {
            title: t('order.model'),
            dataIndex: 'model',
            key: 'model',
            showSorterTooltip: false,
            ellipsis: true
        },
        {
            title: t('order.brand'),
            dataIndex: 'brand',
            key: 'brand',
            showSorterTooltip: false,
            ellipsis: true
        },
        {
            title: t('order.referenceDocumentDate'),
            dataIndex: 'referenceDocumentDate',
            key: 'referenceDocumentDate',
            showSorterTooltip: false,
            ellipsis: true,
            render: (text: any) => (text ? dayjs(text).format(dateFormat) : ""),
        },
        {
            title: t('order.referenceDocumentNumber'),
            dataIndex: 'referenceDocumentNumber',
            key: 'referenceDocumentNumber',
            showSorterTooltip: false,
            ellipsis: true
        }
    ];

    const onRpaCountryModalCancel = () => {
        setRpaCreditNoteCountryModal(false);
        form.resetFields();
    }

    const CountrySelectionForRpaFilesGenerationModal = ({ visibility, onSave }: { visibility: boolean, onSave: () => void }) => {
        return (
            <Modal
                open={visibility}              
                maskClosable={false}
                onCancel={() => {
                    onRpaCountryModalCancel();
                }}
                footer={[
                    <Button key="back" onClick={() => onRpaCountryModalCancel()} disabled={loadingRpaFiles}>
                        {t('general.cancel')}
                    </Button>,
                    <Button htmlType="submit" key="submit" form='rpaCountries' style={{ background: "#011e41", color: "#ffffff" }} loading={loadingRpaFiles} onClick={onSave}>
                        {t('general.continue')}
                    </Button>
                ]}>

                <Result
                    status="warning"
                    style={{ fontSize: 12 }}
                    title={t('order.continueWithRpaFilesGeneration')}
                    icon={<ExclamationCircleOutlined style={{ fontSize: 35 }} />}
                />

                {/* <Form
                    layout='vertical'
                    id="creditNotesRpaCountries"
                    autoComplete="off"
                    form={form}
                    style={{ minHeight: "200px" }}
                    scrollToFirstError={true}
                >

                    <Row gutter={16}>
                        <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 24 }}>
                            <Form.Item
                                label={t('users.country')}
                                name="countries"
                                rules={[{ required: true, message: t('messages.requiredField') }]}
                            >
                                <Select
                                    mode="multiple"
                                    style={{ width: '100%' }}
                                    optionFilterProp="label"
                                    disabled={loadingRpaFiles}
                                    showSearch
                                    allowClear
                                    placeholder={t('general.select')}
                                    options={userCountries.map((item: any) => ({ value: item.id, label: getCountryLabel(item) }))}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                </Form> */}
            </Modal>
        )
    }

    const onGenerateRpaFilesClick = () => {
        if (user.roleCode === roles.admin) {
            setRpaCreditNoteCountryModal(true);
        }
        else {
            generateRpaFiles();
        }
    }

    const closeResultModal = () => {
        setResultModal({
            open: false,
            status: 'info',
            title: '',
            subTitle: '',
        });
    };

    const generateRpaFiles = async () => {
        let payload: any = {};
        let selectedCcountries = [];

        // if (user.roleCode === roles.admin) {
        //     const values = form.getFieldsValue();
        //     selectedCcountries = values.countries;
        // }
        // else {
        //     selectedCcountries.push(user.lastSelectedCountryID ?? null)
        // }

        if (!user.lastSelectedCountryID) {
            message.error(t('messages.rpaCountryMissing'));
            return;
        }

        // (payload as any)['countryIDList'] = selectedCcountries.join(',');
        (payload as any)['countryIDList'] = user.lastSelectedCountryID ?? null;
        (payload as any)['userID'] = user.id;

        setLoadingRpaFiles(true);
        try {
            const respData = await CreditNotesServices.generateCreditNoteRpaFiles(payload);
            if (respData.success) {
                message.success(t('messages.success'));
                getCreditNoteList(filters);
                onRpaCountryModalCancel();
            }
            else {
                if (respData?.errorDetail?.errorCode === 2) {  
                    setResultModal({
                        open: true,
                        status: 'warning',
                        title: t('messages.noRpaCreditNoteToGenerate'),
                        subTitle: "",
                    });
                    onRpaCountryModalCancel();
                }
                else {     
                    setResultModal({
                        open: true,
                        status: 'error',
                        title: t('messages.errorAccured'),
                        subTitle: "",
                    });
                }

            }
        } catch (error) {
            setResultModal({
                open: true,
                status: 'error',
                title: t('messages.errorAccured'),
                subTitle: "",
            });
        }
        finally {
            setLoadingRpaFiles(false);
            setRpaCreditNoteCountryModal(false);
        }
    }


    return (
        <div className='creditNote'>

            <ResultModal
                open={resultModal.open}
                status={resultModal.status}
                title={resultModal.title}
                subTitle={resultModal.subTitle}
                onClose={closeResultModal}
            />

            <CountrySelectionForRpaFilesGenerationModal
                visibility={rpaCreditNoteCountryModal}
                onSave={generateRpaFiles}
            />

            <UploadCreditNoteModal
                visibility={uploadCreditNoteModal}
                onClose={() => setUploadCreditNoteModal(false)}
            />
            
            <Card
                title={t('creditNote.creditNote')}
                extra={
                    <Space direction='horizontal'>
                        <Can permission={permissions.UPLOAD_CREDIT_NOTE_FILE_BUTTON}>
                            <Button onClick={() => setUploadCreditNoteModal(true)} icon={<UploadOutlined />}>{t('creditNote.uploadCreditNote')}</Button>
                        </Can>
                        <Button type='dashed' onClick={() => onClearFilters()}>{t('general.clear')} {t('general.filters')}</Button>

                        <Can permission={permissions.DELETE_ORDER}>
                            <Popconfirm
                                placement="topRight"
                                key="remove"
                                title={t('messages.confirmationApprove')}
                                icon={<WarningOutlined style={{ color: '#f7c924' }} />}
                                okText={t('general.yes')}
                                cancelText={t('general.no')}
                                onConfirm={onSendSelectedOrders}
                            >
                                <Button icon={<DeleteOutlined />} type='dashed' disabled={!selectedCreditNoteIds.length} loading={sendingSelected}>
                                    {t('order.removeSelected')}
                                </Button>
                            </Popconfirm>
                        </Can>
                    </Space>
                }
            >

                <Row gutter={{ lg: 24 }} className='mb10'>
                    <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 24 }} style={{ padding: 0 }}>
                        <Table
                            size='small'
                            className='ordersTable'
                            columns={columns}
                            dataSource={creditNoteList}
                            rowKey="rowKey"
                            rowSelection={rowSelection}
                            loading={loadingCreditNoteList}
                            bordered
                            onChange={(pagination, filters, sorter) =>
                                onPageOrOrderChange(pagination, filters, sorter)
                            }
                            pagination={{
                                pageSize: tablePageSize,
                                showSizeChanger: false,
                                current: pageNumber,
                                total: totalCountRecords || 0
                            }}
                            scroll={{ x: 'max-content' }}
                        />
                    </Col>
                </Row>

                <Row gutter={{ lg: 24 }} className='mb10'>
                    <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 24 }}>
                        <Can permission={permissions.GENERATE_CREDIT_NOTE_RPA_FILES}>
                            <Button disabled={loadingCreditNoteList || !creditNoteList?.length} loading={loadingRpaFiles} type='primary' onClick={() => onGenerateRpaFilesClick()} icon={<MailOutlined />}>{t('order.generateRpaFiles')}</Button>
                        </Can>
                    </Col>
                </Row>
            </Card>
        </div>
    )
}
export default CreditNote;